import { useEffect, useRef, useState } from "react";
import { addOutsidePress, removeOutsidePress } from "./outsidePress";

/**
 * The member's light/dark control — the sun/moon button in the header, beside
 * the notification bell.
 *
 * Shown only when the membership OWNER enabled "Let members switch theme"
 * (brand_settings.theme_mode_switch); otherwise the owner's default is the only
 * appearance and there is nothing to offer. The caller decides that — this
 * component just renders what it is given.
 *
 * A dropdown rather than a straight toggle: the two states are named, so a
 * member sees which one they are on without having to decode an icon.
 */

const SunIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </svg>
);

const MoonIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
  </svg>
);

const OPTIONS = [
  { id: "light", label: "Light", Icon: SunIcon },
  { id: "dark", label: "Dark", Icon: MoonIcon },
];

export default function ThemeToggle({ mode = "light", onChange, primary = "#0274F7", className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Same dismissal contract as the header's other dropdowns (MoreMenu): a click
  // anywhere outside closes it. Escape is handled too — this one can be opened
  // by keyboard from the header's icon row.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    addOutsidePress(onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      removeOutsidePress(onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const Active = mode === "dark" ? MoonIcon : SunIcon;

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Theme: ${mode === "dark" ? "dark" : "light"}`}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex h-9 w-9 items-center justify-center rounded-xl text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 active:scale-95"
        style={open ? { backgroundColor: `${primary}14`, color: primary } : undefined}
      >
        <Active className="h-5 w-5" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute end-0 top-full z-40 mt-1 w-40 rounded-2xl border border-gray-200 bg-surface-raised p-1.5 shadow-xl ring-1 ring-black/5"
        >
          {OPTIONS.map(({ id, label, Icon }) => {
            const active = mode === id;
            return (
              <button
                key={id}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => { setOpen(false); if (!active) onChange?.(id); }}
                className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-sm font-semibold transition-colors ${active ? "" : "text-gray-700 hover:bg-gray-100"}`}
                style={active ? { background: `${primary}0f`, color: primary } : undefined}
              >
                <Icon className={`h-[18px] w-[18px] shrink-0 ${active ? "" : "text-gray-500"}`} />
                <span className="flex-1 text-start">{label}</span>
                {active && (
                  <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
